import { useState } from "react";
import { Card, Button } from "react-bootstrap";
import type { ColumnDef } from "@tanstack/react-table";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListTable,
  BatchActionPill,
  saveErrorReason,
  saveErrorToast,
  type BatchAction,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import { useServiceApprovers, useCollaboratorOptions } from "../api";
import type { ServiceApprover } from "../types";
import "./ApproversTab.css";

export interface ApproversTabProps {
  lobId: string;
  // Approvers come from the parent Line of Business GET's `included`; onChanged
  // triggers that GET's refetch after an add/remove.
  approvers: ServiceApprover[];
  onChanged: () => void;
}

export const availableApproverOptions = (
  options: { value: string; label: string }[],
  takenUserIds: string[]
): { value: string; label: string }[] => {
  const taken = new Set(takenUserIds);
  return options.filter((o) => !taken.has(o.value));
};

export const ApproversTab = ({
  lobId,
  approvers,
  onChanged,
}: ApproversTabProps): JSX.Element => {
  const { add, remove } = useServiceApprovers(lobId);
  const { options } = useCollaboratorOptions();
  const sel = useBatchSelection([]);
  const [selected, setSelected] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const available = availableApproverOptions(
    options,
    approvers.map((r) => r.userId)
  );

  const handleAdd = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await add(selected.value);
      setSelected(null);
      onChanged();
    } catch (err) {
      setToast(saveErrorToast(err, "approver", "add"));
    } finally {
      setBusy(false);
    }
  };

  const handleBatchDelete = async () => {
    const ids = [...sel.selectedIds];
    setBusy(true);
    const failures: BatchFailure[] = [];
    for (const id of ids) {
      try {
        await remove(id);
      } catch (err) {
        failures.push({ reason: saveErrorReason(err) });
      }
    }
    if (failures.length > 0) {
      setToast({
        variant: "danger",
        text: withFailureReasons(
          `Failed to remove ${failures.length} of ${ids.length} approver(s).`,
          failures
        ),
      });
    }
    sel.clear();
    onChanged();
    setBusy(false);
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: handleBatchDelete,
      variant: "danger",
    },
  ];

  const columns: ColumnDef<ServiceApprover>[] = [
    {
      accessorKey: "name",
      header: "Approver",
      cell: ({ row }) => row.original.name,
    },
  ];

  return (
    <div className="admin-form-card legacyPageBleed">
      <Card>
        <Card.Body>
          <div className="approversHeader">
            <span className="adminFormSectionTitle">Approvers</span>
            <BatchActionPill
              selectedCount={sel.selectedIds.length}
              actions={batchActions}
            />
          </div>
          <div className="approversAddRow">
            <div className="approversAddField">
              <RequiredReactSelect
                label="Add an approver"
                value={selected ?? undefined}
                onChange={(opt: any) => setSelected(opt ?? null)}
                options={available}
                placeholder="Select a user…"
              />
            </div>
            <Button
              className="squareGreenButton"
              type="button"
              disabled={!selected || busy}
              onClick={handleAdd}
            >
              Add
            </Button>
          </div>

          <div className="approversTable">
            <AdminListTable
              columns={columns}
              data={approvers}
              selectedIds={sel.selectedIds}
              setSelectedIds={sel.setSelectedIds}
              emptyMessage="No approvers yet."
            />
          </div>
        </Card.Body>
      </Card>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ApproversTab;
